import {
  type AnyRoute,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppProviders } from './providers'
import { Head } from './Head'
import { Analytics } from '@/shared/analytics/Analytics'
import { MarketingLayout } from '@/features/landing/MarketingLayout'
import { HomePage } from '@/features/landing/pages/HomePage'
import { NotFoundPage } from '@/features/landing/pages/NotFoundPage'
import { parseModelerSearch } from '@/features/modeler/search'
import { parseCatalogSearch } from '@/features/catalog/search'
import { parseMessagesSearch, parseSessionsSearch } from '@/features/marketplace/search'

/**
 * Route tree — declared in one place so TanStack can infer the full path union
 * (typed `<Link to>` everywhere). Only the *components* are split out.
 *
 * Splitting strategy (CODEBASE.md → «Бандлы»):
 *   - root chrome + homepage ship in the entry chunk: that is what a cold
 *     visitor from search actually renders;
 *   - every other marketing page is its own chunk;
 *   - the cabinet and the modeler are separate zones, fetched only when one of
 *     their routes is visited. They carry the heavy code (IndexedDB layer, SVG
 *     editor) and must never weigh on the landing's LCP.
 */

const rootRoute = createRootRoute({
  component: () => (
    <AppProviders>
      <Head />
      {/* После <Head />: тот ставит document.title в своём эффекте, а хит
          page_view его читает — братья выполняют эффекты по порядку. */}
      <Analytics />
      <Outlet />
    </AppProviders>
  ),
  notFoundComponent: NotFoundPage,
})


const marketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'marketing',
  component: MarketingLayout,
})

/**
 * Русская витрина — тот же сайт под префиксом `/ru`.
 *
 * Английский главный и живёт в корне; русский получает СВОИ адреса, а не
 * переключатель в памяти браузера. Так у каждой страницы по одному адресу на
 * язык: их можно отправить, положить в выдачу и связать через hreflang — а
 * «тот же URL, но иногда по-русски» ни поисковику, ни собеседнику не объяснить.
 *
 * Дети описаны ОДНИМ списком (`marketingChildrenFor`) и вешаются на оба
 * родителя: две копии страниц разъехались бы на первой же новой.
 */
const ruRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ru',
  component: MarketingLayout,
})



const cabinetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cabinet',
  component: lazyRouteComponent(
    () => import('@/features/cabinet/CabinetLayout'),
    'CabinetLayout',
  ),
})

/**
 * Спецификация языка схем — отдельная страница, а не раздел моделлера: её
 * ссылку отправляют, и открываться она должна без редактора и без аккаунта.
 * Живёт в чанке моделлера, потому что весь словарь берёт из его каталога.
 */
const languageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/language',
  component: lazyRouteComponent(
    () => import('@/features/modeler/pages/LanguagePage'),
    'LanguagePage',
  ),
})

/**
 * Дженерик, а не объединение типов: у TanStack полный путь ребёнка выводится из
 * КОНКРЕТНОГО родителя. Если параметр объявить как `marketingRoute | ruRoute`,
 * вывод схлопывается, и типизированные `<Link to>` теряют половину адресов —
 * что и произошло в первой версии этой правки.
 */
function marketingChildrenFor<TParent extends AnyRoute>(parent: TParent) {
    return [
    createRoute({ getParentRoute: () => parent, path: '/', component: HomePage }),
    createRoute({
      getParentRoute: () => parent,
      path: '/practice',
      component: lazyRouteComponent(() => import('@/features/landing/pages/PracticePage'), 'PracticePage'),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/courses',
      component: lazyRouteComponent(() => import('@/features/landing/pages/CoursesPage'), 'CoursesPage'),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/books',
      component: lazyRouteComponent(() => import('@/features/landing/pages/BooksPage'), 'BooksPage'),
    }),
    // Классификация из книг — СПРАВОЧНИК, а не страница продукта: сюда
    // возвращаются, отсюда цитируют, ссылку на неё отправляют вместе с
    // диагнозом. Ленивый МАРШРУТНЫЙ компонент, как у словаря схемы:
    // `router.load()` дожидается его перед отрисовкой в статику, поэтому все
    // термины попадают в HTML — ради тех, кто не исполняет JavaScript, она и
    // написана.
    createRoute({
      getParentRoute: () => parent,
      path: '/classification',
      component: lazyRouteComponent(
        () => import('@/features/classification/pages/ClassificationPage'),
        'ClassificationPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/app',
      component: lazyRouteComponent(() => import('@/features/landing/pages/AppPage'), 'AppPage'),
    }),
    // Словарь схемы. Ленивый МАРШРУТНЫЙ компонент, а не динамический импорт
    // внутри страницы: `router.load()` дожидается его перед отрисовкой в
    // статику, поэтому весь словарь попадает в HTML для тех, кто не исполняет
    // JavaScript, — ради них он и вынесен из лэндинга на свой адрес.
    createRoute({
      getParentRoute: () => parent,
      path: '/alphabet',
      component: lazyRouteComponent(
        () => import('@/features/showcase/pages/AlphabetPage'),
        'AlphabetPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/certification',
      component: lazyRouteComponent(
        () => import('@/features/landing/pages/CertificationPage'),
        'CertificationPage',
      ),
    }),
    // The catalog is its own zone (`features/catalog`), not a landing page: it
    // talks to the API, holds filter state in the URL and pages through results.
    // It renders inside the marketing chrome because that is where a visitor
    // with no account arrives.
    createRoute({
      getParentRoute: () => parent,
      path: '/find-psychologist',
      validateSearch: parseCatalogSearch,
      component: lazyRouteComponent(() => import('@/features/catalog/pages/CatalogPage'), 'CatalogPage'),
    }),
    // The two addresses a psychologist's page used to live at —
    // /find-psychologist/anna.k and then /p/anna.k. Kept as redirects, forever:
    // a link somebody sent is not ours to break. Production also redirects
    // them at the edge; these routes serve navigations inside the app and
    // development.
    createRoute({
      getParentRoute: () => parent,
      path: '/find-psychologist/$username',
      component: lazyRouteComponent(
        () => import('@/features/catalog/pages/PsychologistPage'),
        'PsychologistPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/p/$username',
      component: lazyRouteComponent(
        () => import('@/features/catalog/pages/PsychologistPage'),
        'PsychologistPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/about',
      component: lazyRouteComponent(() => import('@/features/landing/pages/AboutPage'), 'AboutPage'),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/contact',
      component: lazyRouteComponent(() => import('@/features/landing/pages/ContactPage'), 'ContactPage'),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/privacy',
      component: lazyRouteComponent(() => import('@/features/landing/pages/PrivacyPage'), 'PrivacyPage'),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/terms',
      component: lazyRouteComponent(() => import('@/features/landing/pages/TermsPage'), 'TermsPage'),
    }),
    // The page of ANY account, on the bare root: hub4psy.com/anna.k (owner's
    // decision, 29.09.2026 — no `/p/`). What opens there is decided by one
    // request (`features/user-page`): a listed psychologist's page (the same
    // `ProfilePage`), a person's own page if they switched it on, or «no such
    // page» for everything else. Declared LAST: TanStack ranks static paths
    // above dynamic ones anyway, but the order is kept explicit — and every
    // word that is a route here must be in `Usernames.RESERVED`, or a name
    // taken today shadows a page of the site (`reserved-handles.test.ts`).
    createRoute({
      getParentRoute: () => parent,
      path: '/$username',
      component: lazyRouteComponent(() => import('@/features/user-page/pages/UserPage'), 'UserPage'),
    }),
    ] as const
  }

const cabinetChildren = [
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/OverviewPage'), 'OverviewPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/people',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/PeoplePage'), 'PeoplePage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/people/$personId',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/PersonPage'), 'PersonPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/circles',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/CirclesPage'), 'CirclesPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/groups',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/GroupsPage'), 'GroupsPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/groups/$groupId',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/GroupPage'), 'GroupPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/schemes',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/SchemesPage'), 'SchemesPage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/situations',
    component: lazyRouteComponent(
      () => import('@/features/cabinet/pages/SituationsPage'),
      'SituationsPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/situations/$situationId',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/SituationPage'), 'SituationPage'),
  }),
  // The professional half: creating this profile is what makes an account a
  // psychologist, so the screen lives behind the cabinet's AuthGate with the
  // rest of the owner-only surface.
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice',
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticeDashboardPage'),
      'PracticeDashboardPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/profile',
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticeProfilePage'),
      'PracticeProfilePage',
    ),
  }),
  // Собранная страница: отдельный экран, потому что у него другой контракт
  // сохранения (черновик автосейвом, публикация нажатием) и другой вид отказа.
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/page',
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticePageEditorPage'),
      'PracticePageEditorPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/clients',
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticeClientsPage'),
      'PracticeClientsPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/messages',
    // `?c=<conversationId>` — открыть ИМЕННО эту переписку, а не самую свежую
    // (features/marketplace/search.ts).
    validateSearch: parseMessagesSearch,
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticeMessagesPage'),
      'PracticeMessagesPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/schedule',
    component: lazyRouteComponent(() => import('@/features/practice/pages/SchedulePage'), 'SchedulePage'),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/practice/sessions',
    // `?tab=history` — открыть «Историю»: там прошедшие, которые надо отметить.
    validateSearch: parseSessionsSearch,
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/PracticeSessionsPage'),
      'PracticeSessionsPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    // Not guarded here: the page renders nothing for a non-moderator, and the
    // API answers 404 to one. A route that existed only for admins would be a
    // 404-vs-403 leak in the bundle.
    path: '/moderation',
    component: lazyRouteComponent(
      () => import('@/features/practice/pages/ModerationPage'),
      'ModerationPage',
    ),
  }),
  createRoute({
    getParentRoute: () => cabinetRoute,
    path: '/settings',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/SettingsPage'), 'SettingsPage'),
  }),
] as const

/** `?next=` as the auth screens accept it: a string or nothing. */
const nextOnly = (search: Record<string, unknown>): { next?: string } => ({
  next: typeof search.next === 'string' ? search.next : undefined,
})

/** Auth screens live outside both zones: reachable while signed out. */
const authRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/signin',
    validateSearch: (search: Record<string, unknown>): { next?: string } => ({
      next: typeof search.next === 'string' ? search.next : undefined,
    }),
    component: lazyRouteComponent(() => import('@/features/auth/AuthPages'), 'SignInPage'),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/register',
    // Carried so the "already have an account" link keeps the destination the
    // visitor was heading for — the modeler, when they came from a guest draft.
    validateSearch: (search: Record<string, unknown>): { next?: string } => ({
      next: typeof search.next === 'string' ? search.next : undefined,
    }),
    component: lazyRouteComponent(() => import('@/features/auth/AuthPages'), 'RegisterPage'),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    validateSearch: (search: Record<string, unknown>): { token?: string } => ({
      token: typeof search.token === 'string' ? search.token : undefined,
    }),
    component: lazyRouteComponent(() => import('@/features/auth/AuthPages'), 'VerifyEmailPage'),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/forgot-password',
    component: lazyRouteComponent(() => import('@/features/auth/AuthPages'), 'ForgotPasswordPage'),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password',
    validateSearch: (search: Record<string, unknown>): { token?: string } => ({
      token: typeof search.token === 'string' ? search.token : undefined,
    }),
    component: lazyRouteComponent(() => import('@/features/auth/AuthPages'), 'ResetPasswordPage'),
  }),
] as const

/**
 * Привычные адреса входа — `/login`, `/sign-in`, `/signup`, `/sign-up`.
 *
 * Их набирают руками и получают из закладок других сайтов; раньше каждый был
 * английской 404 без стиля. Это ПЕРЕАДРЕСАЦИЯ, а не второй адрес экрана: у
 * входа один адрес, и `next` едет дальше как есть (санирует его сам экран).
 * Все четыре — на корне, и слово без дефиса лежит в резерве хендлов
 * (`Usernames.RESERVED`): так держит `reserved-handles.test.ts`.
 */
const authAliases = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    validateSearch: nextOnly,
    beforeLoad: ({ search }) => {
      throw redirect({ to: '/signin', search, replace: true })
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-in',
    validateSearch: nextOnly,
    beforeLoad: ({ search }) => {
      throw redirect({ to: '/signin', search, replace: true })
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/signup',
    validateSearch: nextOnly,
    beforeLoad: ({ search }) => {
      throw redirect({ to: '/register', search, replace: true })
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-up',
    validateSearch: nextOnly,
    beforeLoad: ({ search }) => {
      throw redirect({ to: '/register', search, replace: true })
    },
  }),
] as const

const modelerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/psymodeler',
  validateSearch: parseModelerSearch,
  component: lazyRouteComponent(() => import('@/features/modeler/ModelerPage'), 'ModelerPage'),
})

/**
 * An unlisted link to a published scheme: `/s/K3nR7xQm2vB9tLpW`.
 *
 * Its own short path rather than a parameter on `/psymodeler`, because this URL
 * is meant to be pasted into a message — and because a path segment arrives as
 * a string, while search values are JSON-parsed on the way in. Same lazy chunk
 * as the editor: the page decides from the token that it is in view mode.
 */
const sharedSchemeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$token',
  component: lazyRouteComponent(() => import('@/features/modeler/ModelerPage'), 'ModelerPage'),
})

/**
 * The client's half of the marketplace — sessions, messages, psychologists.
 *
 * Its own zone rather than a corner of the cabinet: somebody who only books a
 * session must never download the CRM, which is a psychologist's private notes
 * about people who are not on this platform at all.
 */
const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my',
  component: lazyRouteComponent(() => import('@/features/client/ClientLayout'), 'ClientLayout'),
})

const clientChildren = [
  createRoute({
    getParentRoute: () => clientRoute,
    path: '/',
    component: lazyRouteComponent(() => import('@/features/client/pages/MyDashboardPage'), 'MyDashboardPage'),
  }),
  createRoute({
    getParentRoute: () => clientRoute,
    path: '/sessions',
    validateSearch: parseSessionsSearch,
    component: lazyRouteComponent(() => import('@/features/client/pages/MySessionsPage'), 'MySessionsPage'),
  }),
  createRoute({
    getParentRoute: () => clientRoute,
    path: '/messages',
    validateSearch: parseMessagesSearch,
    component: lazyRouteComponent(() => import('@/features/client/pages/MyMessagesPage'), 'MyMessagesPage'),
  }),
  createRoute({
    getParentRoute: () => clientRoute,
    path: '/psychologists',
    component: lazyRouteComponent(
      () => import('@/features/client/pages/MyPsychologistsPage'),
      'MyPsychologistsPage',
    ),
  }),
  // Настройки аккаунта — те же, что в кабинете, но в рамке зоны клиента:
  // человек, пришедший за встречами, не должен ради смены пароля или выхода
  // попадать в чужую ему «Книгу друзей».
  createRoute({
    getParentRoute: () => clientRoute,
    path: '/settings',
    component: lazyRouteComponent(() => import('@/features/cabinet/pages/SettingsPage'), 'SettingsPage'),
  }),
] as const

/**
 * Дерево маршрутов отдельно от готового роутера: сборка страниц в статику
 * (`entry-prerender.tsx`) создаёт СВОЙ роутер с памятью вместо истории
 * браузера, и ей нужно дерево, а не экземпляр с подгрузкой по наведению.
 */
export const routeTree = rootRoute.addChildren([
  marketingRoute.addChildren(marketingChildrenFor(marketingRoute)),
  ruRoute.addChildren(marketingChildrenFor(ruRoute)),
  cabinetRoute.addChildren(cabinetChildren),
  clientRoute.addChildren(clientChildren),
  createRoute({ getParentRoute: () => rootRoute, path: '/design-system', component: lazyRouteComponent(() => import('@/features/design-system/DesignSystemPage'), 'DesignSystemPage') }),
  modelerRoute,
  languageRoute,
  sharedSchemeRoute,
  ...authRoutes,
  ...authAliases,
])

export const router = createRouter({
  routeTree,
  /** Hovering a link starts fetching its chunk — navigation feels instant. */
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  scrollRestoration: true,
  /**
   * Несуществующий адрес ВНУТРИ раздела (`/ru/cabinet`, `/cabinet/xyz`) роутер
   * отдаёт ближайшему родителю с детьми — витрине, кабинету, зоне клиента. Без
   * этой строки там рисовалась встроенная заглушка TanStack: голое английское
   * «Not Found» без стиля и без выхода, и на русской витрине тоже. Теперь
   * везде одна страница — на языке раздела, с дверями наружу.
   */
  defaultNotFoundComponent: NotFoundPage,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
